/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { t } from '@sportninja/common/i18n'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import Helmet from 'src/components/Helmet'
import GameHeaderSection from 'src/components/GameHeaderSection'
import { AutoPlayButton } from './components'
import LiveScoreWidget from './LiveScoreWidget'
import VideoEventMarkers, {
  videoContainerMarkerStyles,
} from './components/VideoEventMarkers'
import useGameScoreboard from '@sportninja/common/features/game/hooks/useGameScoreboard'
import useGameJerseyColors from '../hooks/useGameJerseyColors'
import {
  computeScoreboardAtTime,
  getLatestScoreboardState,
} from '@sportninja/common/utils/games/scoreboardSync'
import { isValidVideoUrl, isValidStreamingUrl } from 'src/utils/video'
import {
  resolveAssetUrl,
  resolveVideoUrl,
} from '@sportninja/common/utils/assetUrl'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'
import BroadcastStateOverlay from 'src/components/BroadcastStateOverlay'
import StreamSelectorDropdown from 'src/components/StreamSelectorDropdown'
import { ScheduleBreadcrumbs } from 'src/components/ScheduleBreadcrumbs'
import GameInfoBar from './components/GameInfoBar'
import CheersButton from './CheersButton'

const getYoutubeVideoId = (url) => {
  const youtubeUrl = new RegExp(
    '^(https?://)?(www.youtube.com|m.youtube.com|youtu.?be)/.+$'
  )
  if (!url) {
    return ''
  }
  if (!youtubeUrl.test(url)) {
    return ''
  }
  const urlObj = new URL(url)
  const hostname = urlObj.hostname
  if (hostname.includes('youtube.com') || hostname.includes('m.youtube.com')) {
    return urlObj.searchParams.get('v')
  } else if (hostname.includes('youtu.be')) {
    return urlObj.pathname.slice(1) // Remove the leading '/'
  }
  return ''
}

const NewGamePageHeader = ({
  videoUrl,
  broadcastState,
  game,
  organization,
  actions,
  fullGameType,
  isMobile,
  windowWidth,
  onRetry,
  onWatchReplay,
  hasReplayUrl,
  watchingReplay = false,
  error: broadcastError,
  activeStreamCount = 0,
  onSelectStream,
  selectedStreamType,
  homeTeamName,
  visitorTeamName,
  organizationName,
  streams = [],
  homeTeamImage,
  visitorTeamImage,
  wsTimelineEvents,
  wsGameState,
  cheerEvents,
  clearCheerEvent,
}) => {
  const { data: gameEventsData, refetch: refetchGameEvents } =
    useGameScoreboard({
      gameId: game?.id,
      isLive: broadcastState === 'live' && !watchingReplay,
    })
  const scoreboardEvents = gameEventsData?.events

  // Fetch game rosters here (stable parent) so jersey colors don't flash on widget mount/unmount
  const { homeJerseyColor, visitorJerseyColor } = useGameJerseyColors({
    gameId: game?.id,
    game,
  })

  const isHostedOnS3 = videoUrl?.includes('s3.amazonaws.com')
  const isStreamingUrl = isValidStreamingUrl(videoUrl)
  const isHlsStream = videoUrl?.toLowerCase()?.endsWith('.m3u8')
  const isLive = broadcastState === 'live' && !watchingReplay

  const videoRef = useRef(null)
  const videoContainerRef = useRef(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const livestream = videoUrl
    ? game?.livestreams?.find((ls) => ls.stream_url === videoUrl)
    : game?.livestreams?.find((ls) => ls.type === selectedStreamType)
  const streamStartedAt = livestream?.stream_started_at
  const resolvedVideoUrl = resolveVideoUrl(videoUrl)
  // For live: show latest score. For replay: compute score at video current time.
  const initialScoreState = isLive
    ? getLatestScoreboardState(scoreboardEvents)
    : computeScoreboardAtTime({
        currentTimeSec: videoRef.current?.currentTime || 0,
        streamStartedAt,
        isLive: false,
        scoreboardEvents,
      })

  // Use native video element for S3 and streaming URLs (HLS, Mux, etc.)
  const useNativeVideo = isHostedOnS3 || isStreamingUrl
  const [autoPlay, setAutoPlay] = useState(true)
  const [videoError, setVideoError] = useState(false)
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const isValidVideo = isValidVideoUrl(videoUrl)
  const playerRef = useRef(null)
  const hlsRef = useRef(null)

  // Fullscreen the container (not the video) so overlays remain visible
  const toggleFullscreen = useCallback(() => {
    const container = videoContainerRef.current
    if (!container) {
      return
    }
    const fullscreenEl =
      document.fullscreenElement || document.webkitFullscreenElement
    if (fullscreenEl) {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    } else {
      if (container.requestFullscreen) {
        container.requestFullscreen()
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen()
      }
    }
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenEl =
        document.fullscreenElement || document.webkitFullscreenElement
      setIsFullscreen(!!fullscreenEl)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      )
    }
  }, [])

  // Check if thumbnail image is available
  const thumbnailImage = game?.thumbnail_image
  const shouldShowThumbnail = !autoPlay && thumbnailImage

  // Compute props for GameHeaderSection
  const isDesktop = !isMobile
  const title = game.gameNumber
    ? `${t('util:capitalize', { text: 'Game' })} #${game.gameNumber}`
    : game.gameNumber === null || game.gameNumber?.length === 0
    ? t('util:capitalize', { text: 'Game' })
    : `${'Loading'}...`

  const onPlayerStateChange = (event) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
      setAutoPlay(true)
    }
    if (event.data === window.YT.PlayerState.PAUSED) {
      setAutoPlay(false)
    }
  }

  const onPlayerReady = useCallback(
    (event) => {
      console.log('✅ YouTube player ready')
      setIsPlayerReady(true)
      setVideoError(false)
      if (game?.broadcast_video_offset_seconds) {
        console.log(
          'Setting YouTube video offset to:',
          game.broadcast_video_offset_seconds
        )
        event.target.seekTo(game.broadcast_video_offset_seconds, true)
      }
    },
    [game?.broadcast_video_offset_seconds]
  )

  const onPlayerError = (event) => {
    console.error('❌ YouTube player error:', event.data)
    setVideoError(true)
    setIsPlayerReady(false)
  }

  const handleRetryVideo = () => {
    console.log('🔄 handleRetryVideo: Attempting to retry video')
    setVideoError(false)
    setIsPlayerReady(false)
    setAutoPlay(false)
  }

  useEffect(() => {
    if (
      window.YT &&
      !useNativeVideo &&
      videoUrl &&
      isValidVideo &&
      !isPlayerReady &&
      !videoError
    ) {
      try {
        console.log('🎥 Initializing YouTube player')
        playerRef.current = new window.YT.Player('player', {
          height: '720',
          width: windowWidth,
          playerVars: {
            autoplay: 0,
            controls: 1,
            playlist: getYoutubeVideoId(videoUrl),
            rel: 0,
            start: game?.broadcast_video_offset_seconds || 0,
          },
          videoId: videoUrl?.split('v=')[1],
          events: {
            onStateChange: onPlayerStateChange,
            onReady: onPlayerReady,
            onError: onPlayerError,
          },
        })
      } catch (error) {
        console.error('❌ Failed to initialize YouTube player:', error)
        setVideoError(true)
      }
    }
  }, [
    useNativeVideo,
    videoUrl,
    windowWidth,
    game?.broadcast_video_offset_seconds,
    isValidVideo,
    isPlayerReady,
    videoError,
    onPlayerReady,
  ])

  useEffect(() => {
    const playVideo = async () => {
      try {
        if (videoRef.current && autoPlay && !videoError) {
          console.log('🎥 Attempting to play native video')
          await videoRef.current.play()
        }
      } catch (error) {
        console.error('❌ Error playing native video:', error)
        setVideoError(true)
        setAutoPlay(false)
      }
    }

    if (useNativeVideo && !isHlsStream) {
      playVideo()
    }
  }, [videoRef, autoPlay, videoError, useNativeVideo, isHlsStream])

  // Initialize HLS.js for live streaming
  useEffect(() => {
    if (!isHlsStream || !videoUrl || !videoRef.current) {
      return
    }

    // Check if HLS.js is supported (Chrome, Firefox, etc.)
    if (Hls.isSupported()) {
      console.log('🎥 Initializing HLS.js for live stream')
      const hls = new Hls({
        // Live streaming configuration
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
        liveDurationInfinity: true,
        // Start at live edge
        startPosition: -1,
        // Enable low latency mode
        lowLatencyMode: true,
      })

      hls.loadSource(resolvedVideoUrl)
      hls.attachMedia(videoRef.current)

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('✅ HLS manifest parsed, ready to play')
        setIsPlayerReady(true)

        // Seek to live edge immediately for live streams
        if (broadcastState === 'live' && videoRef.current) {
          const seekable = videoRef.current.seekable
          if (seekable.length > 0) {
            const liveEdge = seekable.end(seekable.length - 1)
            console.log('🔴 Seeking to live edge:', liveEdge)
            videoRef.current.currentTime = liveEdge
          }
        }

        // Auto-play when user clicks play
        if (autoPlay && videoRef.current) {
          videoRef.current.play().catch((err) => {
            console.error('❌ HLS autoplay failed:', err)
          })
        }
      })

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('❌ HLS error:', data)
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('🔄 HLS network error, trying to recover...')
              hls.startLoad()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('🔄 HLS media error, trying to recover...')
              hls.recoverMediaError()
              break
            default:
              console.error('❌ Fatal HLS error, cannot recover')
              setVideoError(true)
              hls.destroy()
              break
          }
        }
      })

      hlsRef.current = hls

      return () => {
        console.log('🧹 Cleaning up HLS.js instance')
        hls.destroy()
        hlsRef.current = null
      }
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari has native HLS support
      console.log('🎥 Using native HLS support (Safari)')
      const video = videoRef.current
      video.src = resolvedVideoUrl

      // Seek to live edge when video loads for live streams
      const handleLoadedData = () => {
        if (broadcastState === 'live' && video) {
          const seekable = video.seekable
          if (seekable.length > 0) {
            const liveEdge = seekable.end(seekable.length - 1)
            console.log('🔴 Safari: Seeking to live edge:', liveEdge)
            video.currentTime = liveEdge
          }
        }
      }
      video.addEventListener('loadeddata', handleLoadedData)
      setIsPlayerReady(true)

      return () => {
        video.removeEventListener('loadeddata', handleLoadedData)
      }
    } else {
      console.error('❌ HLS is not supported in this browser')
      setVideoError(true)
    }
  }, [isHlsStream, videoUrl, autoPlay, broadcastState])

  const handlePlayClick = () => {
    setAutoPlay(true)
    if (playerRef.current) {
      playerRef.current.playVideo()
    }
  }

  // Track previous broadcast state to detect transitions
  const prevBroadcastStateRef = useRef(broadcastState)

  // Auto-play when livestream starts (only on transition to 'live')
  useEffect(() => {
    const wasNotLive = prevBroadcastStateRef.current !== 'live'
    const isNowLive = broadcastState === 'live'

    // Only auto-play when transitioning TO 'live' state
    if (wasNotLive && isNowLive && isValidVideo && !videoError) {
      setAutoPlay(true)
      if (playerRef.current) {
        playerRef.current.playVideo()
      }
    }

    prevBroadcastStateRef.current = broadcastState
  }, [broadcastState, isValidVideo, videoError])

  return (
    <>
      <div
        css={css`
          width: ${windowWidth}px;
          display: flex;
          justify-content: center;
        `}
      >
        <div
          css={css`
            width: calc(100% - 36px);
            max-width: 1440px;
          `}
        >
          <ScheduleBreadcrumbs
            organization={organization}
            game={game}
            title={title}
            isDesktop={isDesktop}
          />
        </div>
      </div>
      <div
        css={css`
          width: ${windowWidth}px;
          height: 720px;
          background: #000;
          position: relative;
          .ytp-more-videos-view {
            display: none !important;
          }
        `}
      >
        <Helmet title={title} />
        {/* Show overlay for non-live broadcast states (scheduled, ended without recording, error) */}
        {/* If ended WITH recording (videoUrl available), skip overlay and show video player */}
        {broadcastState &&
        broadcastState !== 'live' &&
        broadcastState !== 'idle' &&
        !(broadcastState === 'ended' && watchingReplay) ? (
          <BroadcastStateOverlay
            state={broadcastState}
            onRetry={onRetry}
            onWatchReplay={onWatchReplay}
            hasReplayUrl={hasReplayUrl}
            error={broadcastError}
          />
        ) : videoError || !isValidVideo ? (
          <div
            css={css`
              width: 100%;
              height: 720px;
              position: absolute;
              top: 0;
              left: 0;
              background: linear-gradient(
                180deg,
                rgba(6, 10, 16, 0.3) 0%,
                #181d26 100%
              );
              display: flex;
              align-items: center;
              justify-content: center;
              flex-direction: column;
              gap: 16px;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 16px 24px;
                background: rgba(0, 0, 0, 0.7);
                border-radius: 8px;
                backdrop-filter: blur(10px);
              `}
            >
              <Icon
                name='exclamation-triangle'
                fontSize={20}
                color={colors.WARNING}
              />
              <span
                css={css`
                  color: ${colors.WHITE};
                  font-size: 16px;
                  font-weight: 500;
                `}
              >
                {!isValidVideo ? 'Invalid video URL' : 'Video unavailable'}
              </span>
            </div>
            {videoError && (
              <button
                css={css`
                  padding: 8px 16px;
                  background: ${colors.PRIMARY};
                  color: ${colors.WHITE};
                  border: none;
                  border-radius: 4px;
                  font-size: 14px;
                  font-weight: 500;
                  cursor: pointer;
                  transition: background 0.2s;
                  &:hover {
                    background: ${colors.PRIMARY_DARK || colors.PRIMARY};
                  }
                `}
                onClick={handleRetryVideo}
              >
                Retry
              </button>
            )}
          </div>
        ) : shouldShowThumbnail ? (
          <img
            src={resolveAssetUrl(thumbnailImage)}
            alt='Game thumbnail'
            css={css`
              width: 100%;
              height: 720px;
              object-fit: cover;
              position: absolute;
              top: 0;
              left: 0;
              border: none;
            `}
          />
        ) : (
          <div
            ref={videoContainerRef}
            onDoubleClick={toggleFullscreen}
            css={css`
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 100%;
              max-width: calc(720px * 16 / 9);
              aspect-ratio: 16 / 9;
              max-height: 720px;
              background: ${colors.BLACK};

              &:fullscreen,
              &:-webkit-full-screen {
                max-width: 100%;
                max-height: 100%;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
              }

              /* Best-effort: hide native fullscreen button where supported */
              video::-webkit-media-controls-fullscreen-button {
                display: none;
              }
            `}
          >
            {/* Inner wrapper keeps overlays aligned to the video area */}
            <div
              css={css`
                position: relative;
                width: 100%;
                height: ${isFullscreen ? 'auto' : '100%'};
                max-width: ${isFullscreen ? 'calc(100vh * 16 / 9)' : 'none'};
                max-height: ${isFullscreen ? '100vh' : 'none'};
                aspect-ratio: 16 / 9;
                ${videoContainerMarkerStyles};
              `}
            >
              {useNativeVideo ? (
                <video
                  ref={videoRef}
                  // Don't set src for HLS streams - hls.js handles loading
                  {...(!isHlsStream && { src: resolvedVideoUrl || '' })}
                  // eslint-disable-next-line react-native/no-inline-styles
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block',
                    background:
                      'linear-gradient(180deg, rgba(6, 10, 16, 0.3) 0%, #060a10 100%)',
                  }}
                  autoPlay={autoPlay}
                  // Only loop S3 videos, not live streams
                  loop={isHostedOnS3}
                  // Only mute S3 videos by default, live streams should have audio
                  muted
                  controls={autoPlay ? true : false}
                  // Enable cross-origin for HLS streams only (S3 MP4s don't need it and will fail CORS)
                  {...(isHlsStream && { crossOrigin: 'anonymous' })}
                  playsInline
                  onPause={() => {
                    // Use a timeout to check if this is a real pause or just seeking
                    setTimeout(() => {
                      if (
                        videoRef.current &&
                        videoRef.current.paused &&
                        !videoRef.current.seeking
                      ) {
                        setAutoPlay(false)
                      }
                    }, 100)
                  }}
                  onLoadedData={() => {
                    // Set initial video position when video loads (only for S3 videos)
                    if (
                      isHostedOnS3 &&
                      game?.broadcast_video_offset_seconds &&
                      videoRef.current
                    ) {
                      videoRef.current.currentTime =
                        game.broadcast_video_offset_seconds
                    }
                  }}
                  onPlay={() => {
                    setAutoPlay(true)
                  }}
                  onError={() => {
                    setVideoError(true)
                  }}
                />
              ) : (
                <div
                  id='player'
                  // eslint-disable-next-line react-native/no-inline-styles
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    background:
                      'linear-gradient(180deg, rgba(6, 10, 16, 0.3) 0%, #181d26 100%)',
                  }}
                />
              )}

              {autoPlay && !videoError && isValidVideo && (
                <LiveScoreWidget
                  game={game}
                  videoUrl={videoUrl}
                  homeTeamImage={homeTeamImage}
                  visitorTeamImage={visitorTeamImage}
                  homeTeamName={homeTeamName}
                  visitorTeamName={visitorTeamName}
                  videoRef={videoRef}
                  selectedStreamType={selectedStreamType}
                  isLive={isLive}
                  wsTimelineEvents={wsTimelineEvents}
                  wsGameState={wsGameState}
                  scoreboardEvents={scoreboardEvents}
                  initialScoreState={initialScoreState}
                  refetchScoreboard={refetchGameEvents}
                  homeJerseyColor={homeJerseyColor}
                  visitorJerseyColor={visitorJerseyColor}
                />
              )}

              {autoPlay && !videoError && isValidVideo && (
                <VideoEventMarkers
                  videoRef={videoRef}
                  scoreboardEvents={scoreboardEvents}
                  streamStartedAt={streamStartedAt}
                  isLive={isLive}
                />
              )}

              {/* LIVE indicator and stream switch button */}
              {(broadcastState === 'live' || activeStreamCount >= 2) && (
                <div
                  css={css`
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    z-index: 10;
                  `}
                >
                  {broadcastState === 'live' && (
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        background-color: rgba(0, 0, 0, 0.6);
                        padding: 6px 10px;
                        border-radius: 4px;
                      `}
                    >
                      <div
                        css={css`
                          width: 8px;
                          height: 8px;
                          border-radius: 50%;
                          background-color: ${colors.RED};
                        `}
                      />
                      <span
                        css={css`
                          color: ${colors.WHITE};
                          font-size: 12px;
                          font-weight: 700;
                          letter-spacing: 1px;
                        `}
                      >
                        LIVE
                      </span>
                    </div>
                  )}

                  {activeStreamCount >= 2 && onSelectStream && (
                    <StreamSelectorDropdown
                      streams={streams}
                      selectedStreamType={selectedStreamType}
                      onSelectStream={onSelectStream}
                      homeTeamName={homeTeamName}
                      visitorTeamName={visitorTeamName}
                      homeTeamImage={homeTeamImage}
                      visitorTeamImage={visitorTeamImage}
                      organizationName={organizationName}
                      isMobile={false}
                    />
                  )}
                </div>
              )}

              {/* Custom fullscreen button */}
              {autoPlay && !videoError && isValidVideo && (
                <button
                  onClick={toggleFullscreen}
                  css={css`
                    position: absolute;
                    bottom: 70px;
                    right: ${isLive ? '68px' : '16px'};
                    z-index: 10;
                    background-color: rgba(0, 0, 0, 0.6);
                    border: none;
                    border-radius: 8px;
                    border: 1px solid ${colors.NEUTRAL_100};
                    width: 40px;
                    height: 40px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: ${colors.WHITE};

                    &:hover {
                      background-color: rgba(0, 0, 0, 0.8);
                    }
                  `}
                >
                  <Icon
                    name={isFullscreen ? 'compress' : 'expand'}
                    color={colors.WHITE}
                    fontSize={24}
                  />
                </button>
              )}

              {/* Cheers button (live only) */}
              {autoPlay && !videoError && isValidVideo && isLive && (
                <CheersButton
                  gameId={game?.id}
                  cheerEvents={cheerEvents}
                  onCheerAnimationComplete={clearCheerEvent}
                />
              )}

              {(!broadcastState ||
                broadcastState === 'idle' ||
                broadcastState === 'live') && (
                <div
                  css={css`
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    ${autoPlay && 'display: none;'}
                    ${!useNativeVideo && !shouldShowThumbnail && 'opacity: 0;'}
                ${!videoError && isValidVideo ? '' : 'display: none;'}
                  `}
                  onClick={handlePlayClick}
                >
                  <span
                    css={css`
                      cursor: pointer;
                    `}
                  >
                    <AutoPlayButton />
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* New shared header – absolute-positioned bottom, with container constraints */}
        <div
          css={css`
            position: absolute;
            bottom: 16px;
            left: 50%;
            transform: translateX(-50%);
            width: calc(100% - 36px);
            max-width: 1440px;
            backdrop-filter: blur(80px);
            background: rgba(12, 16, 24, 0.3);
            padding: 8px;
            border-radius: 8px;
            ${autoPlay && 'display: none !important;'}
          `}
        >
          <GameHeaderSection
            css={css`
              position: static !important;
              width: 100% !important;
              padding: 0 !important;
              margin: 0 !important;
            `}
            organization={organization}
            game={game}
            title={title}
            fullGameType={fullGameType}
            isDesktop={isDesktop}
          />
        </div>
      </div>

      {/* Game date/time and venue info bar */}
      <GameInfoBar
        windowWidth={windowWidth}
        startsAt={game?.startsAt}
        location={game?.location}
        facility={game?.facility}
        fullGameType={fullGameType}
        isMobile={isMobile}
      />
    </>
  )
}

export default NewGamePageHeader
